import Vue, {
  defineComponent,
  toRef,
  watch,
  type Ref,
  type SetupContext,
} from "vue";
import type { EmitFn } from "vue/types/v3-setup-context";

export const ReactiveDialog = defineComponent({
  model: {
    prop: "visible",
    event: "input",
  },
  props: {
    width: { type: [Number, String], default: 600, required: false },
    visible: { type: Boolean, required: false },
  },
  data() {
    return {
      savedFocus: null as HTMLElement | null,
    };
  },
  watch: {
    visible: {
      handler: function (val) {
        if (this.visible) {
          this.storeFocus();
        } else {
          this.setFocus();
        }
      },
    },
  },
  methods: {
    close() {
      this.$emit("input", false);
    },
    open() {
      this.$emit("input", true);
    },
    cancel() {
      this.$emit("cancel");
      this.close();
    },
    accept(value?: any) {
      this.$emit("accept", value);
      this.close();
    },
    storeFocus() {
      this.savedFocus = document.activeElement as HTMLElement;
    },
    setFocus() {
      Vue.nextTick(() => {
        if (this.savedFocus !== null) this.savedFocus.focus();
        this.savedFocus = null;
      });
    },
  },
});
export const useReactiveDialogSetup = (
  visible: Ref<boolean>,
  context: SetupContext<any> // Defining emits gives type SetupContext<("a"|"b"|"c")[]>, so allow for any.
) => {
  return useReactiveDialogScriptSetup(
    visible,
    context.emit,
    toRef(context, "attrs")
  );
};

export const useReactiveDialogScriptSetup = <T = any>(
  visible: Ref<boolean>,
  emit: EmitFn | DialogEvents, // Defining emits gives type EmitFn<("a"|"b"|"c")[]>, so allow for any.
  reactiveAttrs?: Ref<any>
) => {
  let savedFocus: HTMLElement | null = null;

  let close = () => emit("input", false);
  let storeFocus = () => (savedFocus = document.activeElement as HTMLElement);
  let setFocus = () => {
    Vue.nextTick(() => {
      savedFocus?.focus();
      savedFocus = null;
    });
  };

  watch(
    () => visible,
    (val) => {
      if (val) {
        storeFocus();
      } else {
        setFocus();
      }
    }
  );

  return {
    open: () => emit("input", true),
    close,
    accept: (payload?: T) => {
      emit("accept", payload);
      close();
    },
    cancel: () => {
      emit("cancel");
      close();
    },
    storeFocus,
    setFocus,
    reactiveAttrs: reactiveAttrs,
  };
};

/**
 * To use imported types with `defineEmits()` and `defineProps()`, the
 * imported type must be an extension of another type which is defined
 * inside `<script setup>`.
 *
 * Using imported types directly is currently not supported.
 * See Vue documentation: https://vuejs.org/api/sfc-script-setup.html#typescript-only-features
 *
 * @example
 * interface EditSalesNoteDialogEvents<T extends number> extends DialogEvents<T> {}
 * const emit = defineEmits<EditSalesNoteDialogEvents<number>>();
 * // ok
 * emit("accept", 10);
 * // error
 * emit("accept", "test string")
 */
export interface DialogEvents<T = any> {
  (event: "input", value: boolean): void;
  (event: "accept", value: T): void;
  (event: "cancel"): void;
  (event: "close"): void;
}

export interface DialogProps {
  value: boolean;
}
